import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import { MaskedTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { memo, useCallback, useEffect, useState } from "react";

type MaskedTextBoxChangeEvent = {
	value: string;
	syntheticEvent: React.SyntheticEvent;
};

type MaskedTextboxCellProps = GridCustomCellProps & {
	/** The mask pattern for the MaskedTextBox (e.g., "(000) 000-0000" for phone) */
	mask: string;
	/** The placeholder text shown in the input */
	placeholder?: string;
	/** Optional display value formatter for non-edit mode */
	formatValue?: (value: string) => string;
};

export const MaskedTextboxCell = memo((props: MaskedTextboxCellProps) => {
	const { mask, placeholder, formatValue, onChange, tdProps, isInEdit } = props;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Kendo Grid dataItem is typed as any
	const dataItem = props.dataItem;
	const field = props.field ?? "";

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Dynamic field access from Kendo Grid
	const fieldValue = field ? String(dataItem[field] ?? "") : "";

	// Local state to prevent focus loss on every keystroke
	const [localValue, setLocalValue] = useState(fieldValue);

	// Sync local state when fieldValue changes from outside (e.g., cancel edit)
	useEffect(() => {
		setLocalValue(fieldValue);
	}, [fieldValue]);

	const handleChange = useCallback((e: MaskedTextBoxChangeEvent) => {
		setLocalValue(e.value);
	}, []);

	const handleBlur = useCallback(
		(e: MaskedTextBoxEvent) => {
			// Only commit if value actually changed
			if (localValue !== fieldValue && onChange) {
				onChange({
					dataIndex: 0,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Kendo Grid dataItem is typed as any
					dataItem,
					field,
					syntheticEvent: e.syntheticEvent,
					value: localValue,
				});
			}
		},
		[onChange, dataItem, field, localValue, fieldValue],
	);

	const displayValue = formatValue ? formatValue(fieldValue) : fieldValue;

	return (
		<td {...tdProps}>
			{isInEdit ? (
				<MaskedTextBox
					mask={mask}
					onBlur={handleBlur}
					onChange={handleChange}
					placeholder={placeholder}
					value={localValue}
				/>
			) : (
				displayValue
			)}
		</td>
	);
});

MaskedTextboxCell.displayName = "MaskedTextboxCell";
